<script setup lang="ts">
    import { FormInputProps, FormInputData } from '~~/composables/useFormInput';

    const autocomplete = ref(null);
    const props = defineProps<FormInputProps>();
    const inputData: FormInputData = useFormInput(props);
    const streetBoxNumber = ref('');
    const inputManually = ref(false);
    const displayStreetBoxNumber = ref(false);

    // Watch displayStreetBoxNumber to clear streetBoxNumber when disabled
    watch(displayStreetBoxNumber, (newValue) => {
        if (!newValue) {
            streetBoxNumber.value = '';
        }
    });

    const setInputManually = async (value: boolean) => {
        inputManually.value = value;
        if (inputManually.value == false) {
            await nextTick();
            onAutocompleteDisplay();
            return;
        }

        if (typeof inputData.model.value === 'string') {
            inputData.model.value = {
                manual: true,
            };
        } else if (inputData.model.value) {
            inputData.model.value.manual = true;
            inputData.model.value.display = null;
        }
    };

    const setPlace = (place: google.maps.places.PlaceResult) => {
        inputData.form.set(inputData.inputName, useGooglePlaceToAddress(place));
    };
    const getInput = () => {
        return document.getElementById(inputData.inputId.value);
    };

    // Watch the inputData.model value and update the input value manually
    // This is needed because the GMapAutocomplete component doesn't support v-model
    watch(inputData.model, (value) => {
        if (typeof value === 'string') {
            getInput().value = value;
            getInput().focus();
            return;
        }
        getInput().value = value && value.display ? value.display : '';
    });
    watch(streetBoxNumber, (value: string) => {
        if (inputData.model.value) {
            inputData.model.value.streetBoxNumber = value;
        }
    });

    const onAutocompleteDisplay = () => {
        console.log('onAutocompleteDisplay', inputData.model.value);
        if (inputData.model.value && inputData.model.value.streetBoxNumber) {
            streetBoxNumber.value = inputData.model.value.streetBoxNumber;
            displayStreetBoxNumber.value = true;
        }

        if (getInput()) {
            getInput().value = inputData.model?.value?.display ?? '';
            getInput().addEventListener('blur', () => {
                if (getInput().value !== inputData.model?.value?.display) {
                    inputData.form.set(inputData.inputName, '');
                }
            });
        }
    };

    onMounted(() => {
        if (inputData.model.value && inputData.model.value.manual) {
            inputManually.value = true;
        } else {
            onAutocompleteDisplay();
        }
    });
</script>

<template>
    <div class="w-full">
        <FormAddressManual
            v-if="inputManually"
            :input-data="inputData"
            @switch="setInputManually(false)"
        />
        <FormTwoFields
            v-else
            :field2-class="displayStreetBoxNumber ? 'w-[80px]' : 'hidden'"
        >
            <template #field-1>
                <FormElement :input-data="inputData">
                    <div class="relative">
                        <GMapAutocomplete
                            :id="inputData.inputId.value"
                            ref="autocomplete"
                            :placeholder="inputData.placeholder.value"
                            :class="inputData.className.value"
                            :options="{
                                types: ['address'],
                                componentRestrictions: {
                                    country: ['be', 'fr', 'nl', 'lu', 'de'],
                                },
                            }"
                            @place_changed="setPlace"
                        />
                        <div class="absolute top-0 bottom-0 right-0 flex items-center">
                            <span
                                :data-tooltip="$t('form.misc.write_address_manually')"
                                data-tooltip-location="top-right"
                                class="px-3 py-1 text-gray-400 transition-all cursor-pointer hover:text-black"
                                @click.prevent="setInputManually(true)"
                            >
                                <i class="text-sm fa-regular fa-wrench" />
                            </span>
                        </div>
                    </div>
                    <div class="mt-3">
                        <FormSmoothToggleSwitch
                            v-model="displayStreetBoxNumber"
                            :label="$t('form.misc.display_street_box_number')"
                        />
                    </div>
                </FormElement>
            </template>
            <template
                v-if="displayStreetBoxNumber"
                #field-2
            >
                <div :class="{ 'mt-4': !inputData.withoutLabel }">
                    <input
                        v-model="streetBoxNumber"
                        :class="$theme('form.input')"
                        :placeholder="$t('form.labels.streetBoxNumber')"
                    />
                </div>
            </template>
        </FormTwoFields>
    </div>
</template>
